import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Alert, Box, Grid, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import DetailsCard from '../../../ui/layout/DetailsCard';
import ActionButton from '../../../ui/primitives/ActionButton';
import Loader from '../../../ui/primitives/Loader';
import { getRepositoryTrend } from '../../../modules/dashboard';
import { sortTrendData, formatFilterLabel } from '../../../lib/utils';
import QUERY_KEYS from '../../../query/queryKeys';

const CHART_HEIGHT = 320;

const RepositoryTrendSection = ({ dateFilter ,sx = {}}) => {
  const { projectId } = useParams();
  const theme = useTheme();

  const {
    data: trend,
    isLoading: loading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: QUERY_KEYS.project.repositoryTrend(projectId, dateFilter),
    queryFn: () => getRepositoryTrend(projectId, dateFilter),
    enabled: !!projectId,
  });

  const repositoryTrend = useMemo(
    () => sortTrendData(trend?.repository_trend, trend?.grouping, trend?.filter),
    [trend]
  );
  const hasData = repositoryTrend.length > 0;

  const gapPercent = trend?.gap_percent ?? null;
  const isGrowingGap = Boolean(trend?.is_growing_gap);

const subtitle = trend?.filter?.type ? (
  <>
    Test Cases Created vs Executed -{' '}
    <Box component="span" sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}>
      {formatFilterLabel(trend.filter.type)}
    </Box>
  </>
) : (
  'Test Cases Created vs Executed'
);

  if (loading) {
    return (
      <DetailsCard title="Test Repository Intelligence" sx = {sx}>
        <Loader minHeight={CHART_HEIGHT} />
      </DetailsCard>
    );
  }

  if (isError) {
    return (
      <DetailsCard title="Test Repository Intelligence" sx = {sx}>
        <Alert severity="error" action={<ActionButton label="Retry" onClick={() => refetch()} />}>
          {error?.message || 'Failed to load repository trend.'}
        </Alert>
      </DetailsCard>
    );
  }

  return (
    <DetailsCard title="Test Repository Intelligence" contentSx={{ p: 0 }} sx = {sx}>
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={9}>
            <Box
              sx={{
                height: CHART_HEIGHT,
                display: 'flex',
                alignItems: hasData ? 'stretch' : 'center',
                justifyContent: hasData ? 'stretch' : 'center',
              }}
            >
              {hasData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={repositoryTrend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="label"
                      interval="preserveStartEnd"
                      tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    />
                    <YAxis tick={{ fontSize: 12, fill: theme.palette.text.secondary }} />
                    <Tooltip
                      contentStyle={{
                        borderRadius: theme.shape.borderRadius,
                        borderColor: theme.palette.divider,
                        fontSize: theme.typography.body2.fontSize,
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: theme.typography.body2.fontSize }} />
                    <Line
                      type="monotone"
                      dataKey="created"
                      name="Test Cases Created"
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="executed"
                      name="Test Cases Executed"
                      stroke={theme.palette.success.main}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center">
                  No repository activity available for the selected period.
                </Typography>
              )}
            </Box>
          </Grid>

      <Grid item xs={12} md={3}>
        <Box
          sx={{
            p: 2,
            height: '100%',
            borderRadius: `${theme.shape.borderRadius}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            bgcolor: alpha(theme.palette.info.main, 0.06),
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
          }}
        >
          <Typography variant="caption" fontWeight={700} color="info.dark" display="block">
            AI Insight
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {trend?.insight?.message || 'Not enough data yet to generate an insight for this period.'}
          </Typography>
        </Box>
      </Grid>
        </Grid>
      </Box>
    </DetailsCard>
  );
};

export default RepositoryTrendSection;